import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScrollAnimation, useStaggeredAnimation } from '../hooks/useScrollAnimation';
import { usePageSEO } from '../hooks/usePageSEO';
import './Home.css';

const Home = () => {
  const [servicesRef, servicesVisible] = useScrollAnimation(0.2);
  const [featuresRef, featuresVisible] = useScrollAnimation(0.2);
  const [animateText, setAnimateText] = useState(false);
  const navigate = useNavigate();

  usePageSEO(
    'GoToBusiness | Mobile App, Website & Software Development - Noida',
    'GoToBusiness - Mobile application development, website development & software development company. Custom apps, web solutions, Flutter, React. Noida. Get free quote.'
  );
  
  useEffect(() => {
    // Start text animation after component mounts
    const timer = setTimeout(() => {
      setAnimateText(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const renderAnimatedText = (text, className = '') => {
    return text.split('').map((char, index) => (
      <span
        key={index}
        className={`letter ${className} ${animateText ? 'animate' : ''}`}
        style={{ 
          '--letter-index': index,
          animationDelay: `${index * 0.08}s`
        }}
      >
        {char === ' ' ? '\u00A0' : char}
      </span>
    ));
  };

  // Function to handle service card clicks
  const handleServiceClick = (serviceIndex) => {
    const serviceMapping = {
      0: 'website-development',
      1: 'mobile-applications', 
      2: 'software-solutions',
      3: 'unity-gaming'
    };
    
    const categoryParam = serviceMapping[serviceIndex];
    if (categoryParam) {
      navigate(`/projects?category=${categoryParam}`);
    } else {
      navigate('/projects');
    }
  };
  
  const services = [
    {
      icon: "globe",
      title: "Website Development",
      description: "Modern, responsive websites built with the latest technologies. From simple landing pages to complex web applications."
    },
    {
      icon: "mobile-screen",
      title: "Mobile Applications", 
      description: "Cross-platform mobile apps that provide seamless user experiences across iOS and Android devices."
    },
    {
      icon: "code",
      title: "Software Solutions",
      description: "Custom software development tailored to your business needs, from desktop applications to enterprise solutions."
    },
    {
      icon: "gamepad",
      title: "Unity Gaming",
      description: "Engaging 2D and 3D games built with Unity engine, from mobile games to PC and console experiences."
    }
  ];

  const features = [
    { title: "Expert Team", description: "Skilled developers with years of experience in modern technologies" },
    { title: "Quality Assurance", description: "Rigorous testing and quality control for all our deliverables" },
    { title: "Timely Delivery", description: "We respect deadlines and deliver projects on time, every time" },
    { title: "24/7 Support", description: "Continuous support and maintenance for all our solutions" },
    { title: "Competitive Pricing", description: "High-quality solutions at affordable and transparent pricing" },
    { title: "Innovation Focus", description: "Always using the latest technologies and best practices" }
  ];

  const [servicesGridRef, visibleServices] = useStaggeredAnimation(services, 150);
  const [featuresGridRef, visibleFeatures] = useStaggeredAnimation(features, 100);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <span className="hero-badge">Trusted Digital Partner</span>
            <h1 className="hero-title">
              {renderAnimatedText("Transform Your Ideas Into Digital Reality")}
            </h1>
            <p className="hero-description">
              Professional web development, mobile apps, software solutions & Unity games. We deliver scalable, innovative solutions that drive business growth.
            </p>
            <div className="hero-buttons">
              <Link to="/projects" className="btn">View Portfolio</Link>
              <Link to="/contact" className="btn btn-secondary">Get Free Quote</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Professional Trust Metrics */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">50+</span>
              <span className="stat-label">Projects Delivered</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">100+</span>
              <span className="stat-label">Happy Clients</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">5+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">98%</span>
              <span className="stat-label">Client Satisfaction</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="services"
        ref={servicesRef} 
        className={`services section ${servicesVisible ? 'animate' : ''}`}
      >
        <div className="container">
          <span className="section-label">What We Offer</span>
          <h2 className="section-title text-center">Our Services</h2>
          <div ref={servicesGridRef} className="grid grid-3">
            {services.map((service, index) => (
              <div 
                key={index}
                id={`service-${index}`}
                className={`service-card card ${visibleServices.has(index) ? 'animate' : ''}`}
                onClick={() => handleServiceClick(index)}
                style={{ cursor: 'pointer' }}
              >
                <div className="service-icon">
                  <i className={`fas fa-${service.icon}`}></i>
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <div className="service-card-overlay">
                  <span>View Projects →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section 
        ref={featuresRef}
        className={`why-choose section ${featuresVisible ? 'animate' : ''}`} 
        style={{background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'}}
      >
        <div className="container">
          <span className="section-label">Why Choose Us</span>
          <h2 className="section-title text-center">Why Choose GoToBusiness?</h2>
          <div ref={featuresGridRef} className="grid grid-3">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`feature ${visibleFeatures.has(index) ? 'animate' : ''}`}
              >
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta section">
        <div className="container text-center">
          <h2>Ready to Start Your Project?</h2>
          <p>Get a free consultation. We'll discuss your requirements and provide a custom quote within 24 hours.</p>
          <Link to="/contact" className="btn">Get Free Consultation</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;